import React, { useState } from 'react';
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts';
import {
  STACK_COLORS,
  fmtCompact,
  fmtPct,
  getCurrencySymbol,
  buildPivotTrendRows,
  buildCompareData,
  buildContribSnapshotCards,
  aggregateCurrencyBreakdown,
} from '../../../utils/currencyDashboardUtils';
import type { MonthlyRecord } from '../../../hooks/useCurrencyDashboard';
import type { RevenueAmountMode } from '../../../utils/currencyDashboardUtils';

type DeepDiveTab = 'mom_mix' | 'compare';
type CompareKind = 'mom' | 'yoy_month' | 'qoq' | 'h1' | 'h2' | 'yoy';

interface DeepDiveSectionProps {
  filtered: MonthlyRecord[];
  all: MonthlyRecord[];
  /** Single FY, multiple FYs, or `'all'` when no FY filter. */
  fy: string | string[];
  quarter: string;
  month: string;
  revenueMode: RevenueAmountMode;
}

// ─── Snapshot card ────────────────────────────────────────────────────────────
const SnapshotCard: React.FC<{
  ccy: string;
  share: number;
  momRev: number | null;
  momLocal: number | null;
  revenueUp: boolean;
  revenueDown: boolean;
  color: string;
}> = ({ ccy, share, momRev, momLocal, revenueUp, revenueDown, color }) => (
  <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3 flex flex-col gap-1.5">
    <div className="flex items-center gap-2">
      <span className="w-2.5 h-2.5 rounded-full flex-none" style={{ backgroundColor: color }} />
      <span className="text-[11px] font-bold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded uppercase">
        {ccy}
      </span>
      <span className="text-[11px] font-semibold text-slate-500 ml-auto tabular-nums">
        {share.toFixed(1)}%
      </span>
    </div>
    <div className="h-1 w-full rounded-full bg-slate-100 overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${Math.min(100, share)}%`, backgroundColor: color }} />
    </div>
    <div className="flex justify-between text-[10px] text-slate-500">
      <span>
        MoM USD:{' '}
        <span className={`font-semibold ${revenueUp ? 'text-emerald-600' : revenueDown ? 'text-red-500' : 'text-slate-500'}`}>
          {fmtPct(momRev)}
        </span>
      </span>
      <span>
        MoM Local:{' '}
        <span className={`font-semibold ${revenueUp ? 'text-emerald-600' : revenueDown ? 'text-red-500' : 'text-slate-500'}`}>
          {fmtPct(momLocal)}
        </span>
      </span>
    </div>
  </div>
);

// ─── MoM & Mix tab ───────────────────────────────────────────────────────────
const MomMixTab: React.FC<{
  filtered: MonthlyRecord[];
  all: MonthlyRecord[];
  revenueMode: RevenueAmountMode;
}> = ({ filtered, all, revenueMode }) => {
  const raw = filtered.flatMap(m => m._currencyRows);
  const allRaw = all.flatMap(m => m._currencyRows);
  const sorted = [...filtered].sort((a, b) => b.invoice_month_key - a.invoice_month_key);
  const latestYm = sorted.length > 0 ? sorted[0].invoice_month_key : 0;
  const cards = latestYm > 0 ? buildContribSnapshotCards(raw, allRaw, latestYm, revenueMode) : [];
  const breakdown = aggregateCurrencyBreakdown(filtered, revenueMode);
  const topCcys = breakdown.slice(0, 5).map(e => e.currency);
  const pivotRows = buildPivotTrendRows(raw, topCcys, allRaw, revenueMode);

  return (
    <div className="space-y-4">
      {/* Snapshot cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {cards.map((card, idx) => (
          <SnapshotCard
            key={card.ccy}
            {...card}
            color={STACK_COLORS[idx % STACK_COLORS.length]}
          />
        ))}
      </div>

      {/* Trend chart: USD lines left, share% lines right */}
      {pivotRows.length > 0 && (
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={pivotRows} margin={{ top: 4, right: 50, left: 8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 10, fill: '#94A3B8' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              yAxisId="left"
              tickFormatter={v => {
                const n = v as number;
                if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
                if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
                return `$${n}`;
              }}
              tick={{ fontSize: 10, fill: '#94A3B8' }}
              axisLine={false}
              tickLine={false}
              width={55}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tickFormatter={v => `${(v as number).toFixed(0)}%`}
              tick={{ fontSize: 10, fill: '#94A3B8' }}
              axisLine={false}
              tickLine={false}
              width={38}
              domain={[0, 100]}
            />
            <Tooltip
              contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #E2E8F0' }}
              labelStyle={{ fontWeight: 700, marginBottom: 4 }}
              content={({ active, payload, label }) => {
                if (!active || !payload) return null;
                return (
                  <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 min-w-[180px]">
                    <p className="text-[11px] font-bold text-slate-600 mb-2">{label}</p>
                    {payload.map(p => {
                      const nameStr = p.name != null ? String(p.name) : '';
                      const ccyMatch = topCcys.find(c => nameStr.includes(c));
                      const symbol = ccyMatch && nameStr.includes('Rev') ? getCurrencySymbol(ccyMatch) : '';
                      const displayValue = symbol && p.value ? fmtCompact(p.value as number, symbol) : fmtCompact(p.value as number);
                      return (
                        <div key={nameStr || String(p.dataKey ?? p.value)} className="flex items-center justify-between gap-3 mb-1">
                          <div className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full flex-none" style={{ backgroundColor: p.color }} />
                            <span className="text-[11px] text-slate-600">{nameStr}</span>
                          </div>
                          <span className="text-[11px] font-semibold tabular-nums">{displayValue}</span>
                        </div>
                      );
                    })}
                  </div>
                );
              }}
            />
            <Legend
              wrapperStyle={{ fontSize: 11, color: '#64748B' }}
              iconType="circle"
              iconSize={8}
            />
            {topCcys.map((ccy, idx) => (
              <Line
                key={`rev_${ccy}`}
                yAxisId="left"
                type="monotone"
                dataKey={`revUsd_${ccy}`}
                name={`${ccy} Rev`}
                stroke={STACK_COLORS[idx % STACK_COLORS.length]}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            ))}
            {topCcys.map((ccy, idx) => (
              <Line
                key={`share_${ccy}`}
                yAxisId="right"
                type="monotone"
                dataKey={`share_${ccy}`}
                name={`${ccy} Share%`}
                stroke={STACK_COLORS[idx % STACK_COLORS.length]}
                strokeWidth={1.5}
                strokeDasharray="4 2"
                dot={false}
                activeDot={{ r: 3 }}
                legendType="line"
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

// ─── Compare tab ──────────────────────────────────────────────────────────────
const CompareTab: React.FC<{
  all: MonthlyRecord[];
  fy: string | string[];
  quarter: string;
  month: string;
  revenueMode: RevenueAmountMode;
}> = ({ all, fy, quarter, month, revenueMode }) => {
  const [kind, setKind] = useState<CompareKind>('mom');

  const kindOptions: { value: CompareKind; label: string }[] = [
    { value: 'mom', label: 'MoM' },
    { value: 'yoy_month', label: 'YoY Month' },
    { value: 'qoq', label: 'QoQ' },
    { value: 'h1', label: 'H1 vs Prior' },
    { value: 'h2', label: 'H2 vs Prior' },
    { value: 'yoy', label: 'Full Year' },
  ];

  const result = buildCompareData(all, fy, quarter, month, kind, revenueMode);

  const PeriodCard: React.FC<{ label: string; totalUsd: number; entries: typeof result.current.byCurrency; color: string }> = ({
    label, totalUsd, entries, color
  }) => (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex-1">
      <div className="px-4 py-2.5 border-b border-slate-100">
        <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">{label}</p>
        <p className="text-lg font-bold text-[#0D0D0D] tabular-nums mt-0.5">{fmtCompact(totalUsd)}</p>
      </div>
      <div className="px-4 py-3 space-y-2.5">
        {entries.map((e, idx) => (
          <div key={e.currency} className="space-y-0.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full flex-none" style={{ backgroundColor: STACK_COLORS[idx % STACK_COLORS.length] }} />
                <span className="text-[11px] font-semibold text-slate-600">{e.currency}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] tabular-nums text-slate-700">{fmtCompact(e.usdAmount)}</span>
                <span className="text-[10px] text-slate-600 tabular-nums w-10 text-right">{e.pctShare.toFixed(1)}%</span>
              </div>
            </div>
            <div className="h-1 w-full rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{ width: `${Math.min(100, e.pctShare)}%`, backgroundColor: color }}
              />
            </div>
          </div>
        ))}
        {entries.length === 0 && <p className="text-sm text-slate-600 text-center py-2">No data</p>}
      </div>
    </div>
  );

  const changePct = result.prior.totalUsd > 0
    ? ((result.current.totalUsd - result.prior.totalUsd) / result.prior.totalUsd) * 100
    : null;

  return (
    <div className="space-y-4">
      {/* Kind selector */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {kindOptions.map(opt => (
          <button
            key={opt.value}
            onClick={() => setKind(opt.value)}
            className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${
              kind === opt.value
                ? 'bg-blue-500 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Change badge */}
      {changePct != null && (
        <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold ${
          changePct >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'
        }`}>
          {changePct >= 0 ? '↑' : '↓'} {Math.abs(changePct).toFixed(1)}% vs prior period
        </div>
      )}

      {/* Two columns */}
      <div className="flex gap-3 flex-wrap lg:flex-nowrap">
        <PeriodCard
          label={result.current.label}
          totalUsd={result.current.totalUsd}
          entries={result.current.byCurrency}
          color="#2563EB"
        />
        <PeriodCard
          label={result.prior.label}
          totalUsd={result.prior.totalUsd}
          entries={result.prior.byCurrency}
          color="#94A3B8"
        />
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
export const DeepDiveSection: React.FC<DeepDiveSectionProps> = ({
  filtered,
  all,
  fy,
  quarter,
  month,
  revenueMode,
}) => {
  const [activeTab, setActiveTab] = useState<DeepDiveTab>('mom_mix');

  const tabs: { value: DeepDiveTab; label: string }[] = [
    { value: 'mom_mix', label: 'MoM & Mix' },
    { value: 'compare', label: 'Compare' },
  ];

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
      {/* Card header */}
      <div className="flex items-center justify-between px-4 pt-3 pb-0 border-b border-slate-100">
        <div className="flex items-center gap-2 pb-2">
          <div className="w-0.5 h-4 rounded-full bg-indigo-500 flex-none" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
            Deep Dive
          </span>
        </div>
        {/* Tab bar */}
        <div className="flex items-end gap-0">
          {tabs.map(tab => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`px-4 py-2 text-[12px] font-semibold border-b-2 transition-colors ${
                activeTab === tab.value
                  ? 'border-blue-500 text-blue-600 bg-blue-50/30'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50/50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="p-4">
        {activeTab === 'mom_mix' && (
          <MomMixTab filtered={filtered} all={all} revenueMode={revenueMode} />
        )}
        {activeTab === 'compare' && (
          <CompareTab all={all} fy={fy} quarter={quarter} month={month} revenueMode={revenueMode} />
        )}
      </div>
    </div>
  );
};

export default DeepDiveSection;
